import { Doc } from '@dadohr/shared/domainTypes/document';
import { DoceboIntegrationConfig } from '@dadohr/shared/domainTypes/integrations/docebo';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogContent,
  MenuItem,
  TextField
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useEffect, useRef, useState } from 'react';
import { Functionality } from '../..';
import { ButtonWithPromise } from '../../../../../../components/ButtonWithPromise';
import { CustomInputLabel } from '../../../../../../components/CustomInputLabel';
import { DialogActionsWithSlots } from '../../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import * as ENVS from '../../../../../../env.json';
import { useUnsafeTranslation } from '../../../../../../i18next';
import {
  FlexContainer,
  FlexContainerVertical
} from '../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../services/currentUser';
import { IntegrationData } from '../../components/IntegrationData';
import {
  IntegrationConfigurationButton,
  IntegrationSetupDialogProperties,
  IntegrationSetupDialogWithConfigProperties,
  masked
} from '../common';
import {
  removeDoceboConfig,
  setupDoceboIntegration,
  useDoceboConfig
} from './service';

const NAME = 'Docebo';
const ALERT_MESSAGE  = 'Integrate Dado and Docebo to assign employees to courses and track completion.';

type DoceboAuthMethod = NonNullable<DoceboIntegrationConfig['authMethod']>;

// Undefined authMethod on a stored config ⇒ the legacy PASSWORD grant.
const authMethodOf = (
  config?: Doc<DoceboIntegrationConfig> | null
): DoceboAuthMethod => config?.data.authMethod ?? 'PASSWORD';

const AUTH_METHOD_OPTIONS: {
  value: DoceboAuthMethod;
  label: string;
}[] = [
  {
    value: 'PASSWORD',
    label: 'Username & password (OAuth2)',
  },
  {
    value: 'CLIENT_CREDENTIALS',
    label: 'Client credentials (server-to-server)',
  }
];

const Content = styled(DialogContent)((p) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: p.theme.spacing(2)
}));

const ExistingSetupDialog = ({
  config,
  open,
  onClose
}: {
  config: Doc<DoceboIntegrationConfig>;
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();

  // `env.json` is generated from secrets per environment; a freshly-provisioned
  // env may not carry the docebo key yet. Fall back to the app's own origin +
  // the hosting rewrite so the dialog never crashes on a missing key.
  const webhookUrl =
    (ENVS as { docebo?: { webhookUrl?: string } }).docebo?.webhookUrl ||
    `${window.location.origin}/api/docebo/webhook`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t(ALERT_MESSAGE)}
        </Alert>
        <IntegrationData>
          <strong>{t('Subdomain')}</strong>
          <div>{config.data.subdomain || ''}</div>
          <strong>{t('Authentication')}</strong>
          <div>
            {AUTH_METHOD_OPTIONS.find((o) => o.value === authMethodOf(config))
              ?.label || authMethodOf(config)}
          </div>
          <strong>{t('Client ID')}</strong>
          <div>{config.data.clientId || ''}</div>
          <strong>{t('Client Secret')}</strong>
          <div>{masked(config.data.clientSecret)}</div>
          {authMethodOf(config) === 'PASSWORD' && (
            <>
              <strong>{t('Refresh Token')}</strong>
              <div>{masked(config.data.refreshToken)}</div>
            </>
          )}
          {config.data.webhook && (
            <>
              <strong>{t('Webhook URL')}</strong>
              <div>{webhookUrl}</div>
              <strong>{t('Webhook Username')}</strong>
              <div>{config.data.webhook.username}</div>
              <strong>{t('Webhook Password')}</strong>
              <div>{config.data.webhook.password}</div>
            </>
          )}
        </IntegrationData>
      </Content>
      <DialogActionsWithSlots
        left={
          <ButtonWithPromise
            variant="contained"
            color="secondary"
            onClick={() => removeDoceboConfig(config.data.orgId)}
            pending={`${t('Removing')}...`}
          >
            {t('Remove')}
          </ButtonWithPromise>
        }
        right={
          <Button color="primary" onClick={onClose}>
            {t('Close')}
          </Button>
        }
      />
    </Dialog>
  );
};

const SetupDialog = ({
  config,
  open,
  onClose,
  onSuccessfulIntegration
}: IntegrationSetupDialogWithConfigProperties<DoceboIntegrationConfig>) => {
  const { id: userId, org } = useCurrentUser();
  const { t } = useUnsafeTranslation();
  // Configured when a client secret is stored AND, for the PASSWORD grant, a
  // refresh token exists (CLIENT_CREDENTIALS has no refresh token).
  const isSetup =
    !!config?.data.clientSecret &&
    (authMethodOf(config) === 'CLIENT_CREDENTIALS' ||
      !!config?.data.refreshToken);
  const previousConfig = useRef<Doc<DoceboIntegrationConfig> | null>();

  const [authMethod, setAuthMethod] = useState<DoceboAuthMethod>(
    authMethodOf(config)
  );
  const [state, setState] = useState<{
    subdomain: string;
    clientId: string;
    clientSecret: string;
    username: string;
    password: string;
  }>({
    subdomain: config?.data.subdomain || '',
    clientId: config?.data.clientId || '',
    clientSecret: config?.data.clientSecret || '',
    username: '',
    password: ''
  });

  //This is a bit of a hack to detect when the config active status changes so we can show the modal dialog
  //here even if the integration is completed on another browser tab.
  useEffect(() => {
    if (
      previousConfig.current?.data &&
      config?.data.active &&
      !previousConfig.current?.data.active
    ) {
      onSuccessfulIntegration();
    }
    previousConfig.current = config;
  }, [config, onSuccessfulIntegration]);

  if (!config) {
    return null;
  }

  if (isSetup) {
    return (
      <ExistingSetupDialog config={config} open={open} onClose={onClose} />
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitleWithTypography>
        {NAME} {t('org.integrations.integration')}
      </DialogTitleWithTypography>
      <Content>
        <Alert severity="info" icon={false}>
          {t(ALERT_MESSAGE)}
        </Alert>

        <FlexContainerVertical spacing={2}>
          <CustomInputLabel label={t('Authentication method')} fullWidth>
            <TextField
              select
              variant="outlined"
              value={authMethod}
              onChange={(ev) =>
                setAuthMethod(ev.target.value as DoceboAuthMethod)
              }
              fullWidth
            >
              {AUTH_METHOD_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {t(o.label)}
                </MenuItem>
              ))}
            </TextField>
          </CustomInputLabel>
          <CustomInputLabel label={t('Subdomain')} fullWidth>
            <TextField
              variant="outlined"
              value={state.subdomain}
              onChange={(ev) =>
                setState((s) => ({ ...s, subdomain: ev.target.value }))
              }
              helperText={
                <>
                  {t('e.g if you access Docebo via')} https://acme.docebosaas.com
                  {t(', your subdomain is')} <b>acme</b>
                </>
              }
              fullWidth
            />
          </CustomInputLabel>
          <CustomInputLabel label={t('Client ID')} fullWidth>
            <TextField
              variant="outlined"
              value={state.clientId}
              onChange={(ev) =>
                setState((s) => ({ ...s, clientId: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>

          <CustomInputLabel label={t('Client Secret')} fullWidth>
            <TextField
              variant="outlined"
              value={state.clientSecret}
              type="password"
              onChange={(ev) =>
                setState((s) => ({ ...s, clientSecret: ev.target.value }))
              }
              fullWidth
            />
          </CustomInputLabel>

          {authMethod === 'PASSWORD' && (
            <>
              <CustomInputLabel label={t('Username')} fullWidth>
                <TextField
                  variant="outlined"
                  value={state.username}
                  onChange={(ev) =>
                    setState((s) => ({ ...s, username: ev.target.value }))
                  }
                  fullWidth
                />
              </CustomInputLabel>

              <CustomInputLabel label={t('Password')} fullWidth>
                <TextField
                  variant="outlined"
                  value={state.password}
                  type="password"
                  onChange={(ev) =>
                    setState((s) => ({ ...s, password: ev.target.value }))
                  }
                  fullWidth
                />
              </CustomInputLabel>
            </>
          )}
        </FlexContainerVertical>
      </Content>
      <DialogActionsWithSlots
        right={
          <FlexContainer justifyContent="flex-end">
            <Button onClick={onClose}>{t('Cancel')}</Button>
            <ButtonWithPromise
              variant="contained"
              color="primary"
              pending={`${t('Saving')}...`}
              disabled={
                !state.subdomain ||
                !state.clientId ||
                !state.clientSecret ||
                (authMethod === 'PASSWORD' &&
                  (!state.username || !state.password))
              }
              onClick={() =>
                setupDoceboIntegration(org.id, userId, {
                  subdomain: state.subdomain,
                  clientId: state.clientId,
                  clientSecret: state.clientSecret,
                  authMethod,
                  ...(authMethod === 'PASSWORD'
                    ? { username: state.username, password: state.password }
                    : {})
                })
              }
            >
              {t('Set up')}
            </ButtonWithPromise>
          </FlexContainer>
        }
      />
    </Dialog>
  );
};

export const DoceboSettingsButton = ({
  functionalities,
  interactive
}: {
  functionalities: Functionality[];
  interactive: boolean;
}) => {
  const { id: userId, org } = useCurrentUser();
  const [config] = useDoceboConfig(org.id, userId);
  const cfg = config || null;

  return (
    <>
      {
        <IntegrationConfigurationButton
          name={NAME}
          functionalities={functionalities}
          logoUrl="/images/integrations-logos/docebo-full-logo.svg"
          active={!!cfg?.data.active}
          interactive={interactive}
          renderSetupDialog={({
            open,
            onClose,
            onFailure,
            onSuccessfulIntegration
          }: IntegrationSetupDialogProperties) => (
            <SetupDialog
              config={cfg}
              open={open}
              onClose={onClose}
              onFailure={onFailure}
              onSuccessfulIntegration={onSuccessfulIntegration}
            />
          )}
        />
      }
    </>
  );
};
